import React, { useEffect, useRef, useState } from 'react';
import { readAuthorSetupIdentity } from '../../auth/authorSetup';

const PROFILE_API = process.env.REACT_APP_FETCH_AUTHOR_SHIPPING_API_URL || 'https://1mgl8j9tf2.execute-api.us-west-2.amazonaws.com/default/author-shipping-address';
const FIELDS = [
  ['fullName', 'Full name', 'name', 120],
  ['address1', 'Address line 1', 'address-line1', 200],
  ['address2', 'Address line 2 (optional)', 'address-line2', 200],
  ['city', 'City', 'address-level2', 100],
  ['state', 'State / province / region', 'address-level1', 100],
  ['zip', 'ZIP / postal code', 'postal-code', 20],
  ['country', 'Country code', 'country', 2],
  ['phone', 'Phone number (optional)', 'tel', 40],
];
const EMPTY = Object.fromEntries(FIELDS.map(([key]) => [key, key === 'country' ? 'US' : '']));
function savedAddress(data) {
  if (!data || typeof data.version !== 'string' || !data.version || !Object.hasOwn(data, 'shippingAddress')) throw new Error('Unable to read your saved address. Please retry.');
  const value = data.shippingAddress;
  if (value !== null && (typeof value !== 'object' || Array.isArray(value))) throw new Error('Unable to read your saved address. Please retry.');
  return Object.fromEntries(FIELDS.map(([key]) => {
    const text = value?.[key] ?? EMPTY[key];
    if (typeof text !== 'string') throw new Error('Unable to read your saved address. Please retry.');
    return [key, text];
  }));
}
async function request(identity, body) {
  const response = await fetch(PROFILE_API, {
    method: body ? 'POST' : 'GET',
    headers: { Authorization: 'Bearer ' + identity.token, 'Content-Type': 'application/json' },
    ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(20000),
  });
  let data;
  try { data = await response.json(); } catch { throw new Error('Unable to read the response. Reload the saved address before retrying.'); }
  if (!response.ok) throw new Error(data?.error || 'Unable to access your shipping address. Please retry.');
  return { address: savedAddress(data), version: data.version };
}

export default function Settings({ shouldScrollToShippingAddress = false }) {
  const [address, setAddress] = useState(EMPTY);
  const [baseline, setBaseline] = useState('');
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const section = useRef(null), scrolled = useRef(false), owner = useRef(null), lock = useRef(false), generation = useRef(0);
  const dirty = ready && JSON.stringify(address) !== baseline;
  useEffect(() => {
    const current = ++generation.current;
    setLoading(true); setReady(false); setError(''); setStatus('');
    readAuthorSetupIdentity().then(async identity => ({ identity, result: await request(identity) }))
      .then(({ identity, result }) => {
        if (generation.current !== current) return;
        owner.current = identity.userSub;
        setAddress(result.address); setBaseline(JSON.stringify(result.address)); setVersion(result.version); setReady(true);
      }).catch(e => { if (generation.current === current) setError(e.message || 'Unable to load your shipping address. Please retry.'); })
      .finally(() => { if (generation.current === current) setLoading(false); });
    return () => { generation.current = current + 1; };
  }, [attempt]);
  useEffect(() => {
    if (!shouldScrollToShippingAddress || scrolled.current || !section.current) return;
    section.current.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    scrolled.current = true;
  }, [shouldScrollToShippingAddress]);
  useEffect(() => {
    const unload = event => { if (dirty || lock.current) { event.preventDefault(); event.returnValue = ''; } };
    const leave = event => { if (lock.current || (dirty && !window.confirm('Discard your unsaved shipping address changes?'))) event.preventDefault(); };
    window.addEventListener('beforeunload', unload);
    window.addEventListener('bookish:before-navigation', leave);
    return () => { window.removeEventListener('beforeunload', unload); window.removeEventListener('bookish:before-navigation', leave); };
  }, [dirty]);
  const reload = () => {
    if (lock.current || (dirty && !window.confirm('Reload the saved address and discard your edits?'))) return;
    setAttempt(value => value + 1);
  };
  const save = async event => {
    event.preventDefault();
    if (!ready || loading || lock.current || !dirty) return;
    const clean = Object.fromEntries(FIELDS.map(([key]) => [key, address[key].trim()]));
    clean.country = clean.country.toUpperCase();
    if (!['fullName','address1','city','state','zip','country'].every(key => clean[key]) || !/^[A-Z]{2}$/.test(clean.country)) {
      setError('Complete your name, street, city, state or region, postal code, and two-letter country code.'); return;
    }
    if (clean.country === 'US' && !/^\d{5}(-\d{4})?$/.test(clean.zip)) { setError('Enter a valid US ZIP code (12345 or 12345-6789).'); return; }
    lock.current = true; setSaving(true); setError(''); setStatus('');
    const current = generation.current;
    try {
      const identity = await readAuthorSetupIdentity();
      if (generation.current !== current) return;
      if (identity.userSub !== owner.current) throw new Error('Your Author account changed. Reload the saved address before continuing.');
      const result = await request(identity, { shippingAddress: clean, version });
      if (generation.current !== current) return;
      setAddress(result.address); setBaseline(JSON.stringify(result.address)); setVersion(result.version);
      setStatus('Shipping address saved. Return to Orders and refresh before requesting a new postage quote.');
    } catch (e) { if (generation.current === current) setError(e.message || 'Unable to save. Reload the saved address before retrying.'); }
    finally { lock.current = false; if (generation.current === current) setSaving(false); }
  };
  return <section id="shipping-address-section" aria-labelledby="shipping-heading" ref={section} className="rounded-xl border border-bookish-purple/20 bg-bookish-cream/30 p-5 text-bookish-purple">
    <h2 id="shipping-heading" className="text-xl font-serif font-bold">Shipping Address</h2>
    <p className="mt-2 mb-4 text-sm">Use the sender and return address for your shipping labels. This address appears on labels; choose an address where you can receive returns.</p>
    {loading && <p role="status">Loading shipping address…</p>}
    {error && <p role="alert" className="mb-3 text-sm text-red-700">{error}</p>}
    {ready && <form onSubmit={save}>
      <p className="mb-3 text-sm">All fields are required except address line 2 and phone number.</p>
      <fieldset disabled={saving} className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <legend className="sr-only">Sender address</legend>
        {FIELDS.map(([key, label, autocomplete, max]) => <label key={key} className={'text-sm font-semibold' + (key.startsWith('address') ? ' md:col-span-2' : '')}>
          {label}
          <input name={key} type={key === 'phone' ? 'tel' : 'text'} autoComplete={autocomplete} maxLength={max}
            required={!['address2','phone'].includes(key)} value={address[key]}
            aria-describedby={key === 'country' ? 'country-help' : undefined}
            onChange={e => { setAddress(previous => ({ ...previous, [key]: e.target.value })); setStatus(''); setError(''); }}
            className="mt-1 min-h-[44px] w-full rounded border border-bookish-purple/30 px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-bookish-purple" />
          {key === 'country' && <span id="country-help" className="mt-1 block font-normal">Two letters, such as US or CA. International labels may require arranging postage with your carrier.</span>}
        </label>)}
      </fieldset>
      <button type="submit" disabled={saving || !dirty} className="mt-5 min-h-[44px] rounded-md bg-bookish-purple px-4 py-2 font-semibold text-white disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4">{saving ? 'Saving…' : 'Save address'}</button>
      <p role="status" className="mt-3 text-sm">{status || (dirty ? 'You have unsaved address changes.' : '')}</p>
    </form>}
    {!loading && <button type="button" onClick={reload} disabled={saving} className="mt-3 min-h-[44px] underline disabled:opacity-50 focus-visible:outline focus-visible:outline-2">{ready ? 'Reload saved address' : 'Retry loading address'}</button>}
  </section>;
}
